import React, { useState, useEffect } from 'react';
import { XMarkIcon, CalendarIcon, TagIcon, CurrencyDollarIcon, UsersIcon } from '@heroicons/react/24/outline';
import ChoicesSelect from '../ChoicesSelect';
import useClickOutside from '../../hooks/useClickOutside';

interface Promotion {
  id?: number;
  name: string;
  slug?: string;
  description: string;
  type: 'percentage' | 'fixed_amount' | 'buy_x_get_y';
  value: number;
  minimum_amount?: number;
  maximum_discount?: number;
  start_date: string;
  end_date: string;
  usage_limit?: number;
  usage_limit_per_customer?: number;
  used_count?: number;
  applies_to: 'all' | 'specific_products' | 'specific_categories' | 'specific_brands';
  conditions?: any;
  is_active: boolean;
  products_count?: number;
  created_at?: string;
  status?: string;
}

interface PromotionModalProps {
  isOpen: boolean;
  onClose: () => void;
  promotion?: Promotion | null;
  mode: 'create' | 'edit' | 'view';
  onSave: (promotion: Promotion) => void;
}

const PromotionModal: React.FC<PromotionModalProps> = ({
  isOpen,
  onClose,
  promotion,
  mode,
  onSave,
}) => {
  const modalRef = useClickOutside(onClose);
  const [formData, setFormData] = useState<Promotion>({
    name: '',
    description: '',
    type: 'percentage',
    value: 0,
    minimum_amount: 0,
    maximum_discount: 0,
    start_date: '',
    end_date: '',
    usage_limit: 0,
    usage_limit_per_customer: 0,
    applies_to: 'all',
    conditions: {},
    is_active: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (promotion && (mode === 'edit' || mode === 'view')) {
      setFormData({
        ...promotion,
        start_date: promotion.start_date ? new Date(promotion.start_date).toISOString().split('T')[0] : '',
        end_date: promotion.end_date ? new Date(promotion.end_date).toISOString().split('T')[0] : '',
      });
    } else if (mode === 'create') {
      setFormData({
        name: '',
        description: '',
        type: 'percentage',
        value: 0,
        minimum_amount: 0,
        maximum_discount: 0,
        start_date: '',
        end_date: '',
        usage_limit: 0,
        usage_limit_per_customer: 0,
        applies_to: 'all',
        conditions: {},
        is_active: true,
      });
    }
    setErrors({});
  }, [promotion, mode, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : 
              type === 'number' ? parseFloat(value) || 0 : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Promotion name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (formData.value <= 0) {
      newErrors.value = 'Value must be greater than 0';
    }

    if (formData.type === 'percentage' && formData.value > 100) {
      newErrors.value = 'Percentage cannot exceed 100%';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }

    if (!formData.end_date) {
      newErrors.end_date = 'End date is required';
    }

    if (formData.start_date && formData.end_date && formData.start_date >= formData.end_date) {
      newErrors.end_date = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (mode === 'view') return;
    
    if (validateForm()) {
      // Clean up the data before sending
      const cleanedData = {
        ...formData,
        minimum_amount: formData.minimum_amount || undefined,
        maximum_discount: formData.maximum_discount || undefined,
        usage_limit: formData.usage_limit || undefined,
        usage_limit_per_customer: formData.usage_limit_per_customer || undefined,
      };
      onSave(cleanedData);
    }
  };

  const getPromotionStatus = (promotion: any): string => {
    if (!promotion?.is_active) {
      return 'Inactive';
    }
    
    const now = new Date();
    const startDate = new Date(promotion.start_date);
    const endDate = new Date(promotion.end_date);
    
    if (startDate > now) {
      return 'Upcoming';
    }
    
    if (endDate < now) {
      return 'Expired';
    }
    
    return 'Active';
  };

  const getStatusBadge = (status?: string) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      expired: 'bg-red-100 text-red-800',
      upcoming: 'bg-blue-100 text-blue-800',
    };
    
    return badges[status?.toLowerCase() as keyof typeof badges] || badges.inactive;
  };



  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div ref={modalRef} className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <TagIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {mode === 'create' && 'Create New Promotion'}
                {mode === 'edit' && 'Edit Promotion'}
                {mode === 'view' && 'Promotion Details'}
              </h2>
              <p className="text-sm text-gray-500">
                {mode === 'create' && 'Add a new promotional offer'}
                {mode === 'edit' && 'Update promotion information'}
                {mode === 'view' && 'View promotion details and statistics'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {mode === 'view' && promotion && (
            <div className="mb-6 p-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">{promotion.name}</h3>
                  <p className="text-purple-100">{promotion.description}</p>
                </div>
                <div className="text-right">
                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(getPromotionStatus(promotion))}`}>
                    {getPromotionStatus(promotion)}
                  </span>
                  <div className="mt-2 text-sm text-purple-100">
                    Used: {promotion?.used_count || 0} times
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Basic Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Promotion Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={mode === 'view'}
                  className={`w-full px-3 py-2 border rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  } ${mode === 'view' ? 'bg-gray-50' : ''}`}
                  placeholder="Enter promotion name"
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  disabled={mode === 'view'}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  } ${mode === 'view' ? 'bg-gray-50' : ''}`}
                  placeholder="Enter promotion description"
                />
                {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Promotion Type *
                </label>
                <ChoicesSelect
                  options={[
                    { value: 'percentage', label: 'Percentage Discount' },
                    { value: 'fixed_amount', label: 'Fixed Amount Discount' },
                    { value: 'buy_x_get_y', label: 'Buy X Get Y' }
                  ]}
                  value={formData.type}
                  onChange={(value) => {
                    const event = { target: { name: 'type', value } };
                    handleInputChange(event as any);
                  }}
                  placeholder="Select Promotion Type"
                  disabled={mode === 'view'}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {formData.type === 'percentage' ? 'Discount Percentage (%)' : 'Discount Amount (Ks)'} *
                </label>
                <div className="relative">
                  <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="number"
                    name="value"
                    value={formData.value}
                    onChange={handleInputChange}
                    disabled={mode === 'view'}
                    min="0"
                    max={formData.type === 'percentage' ? '100' : undefined}
                    step="0.01"
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] ${
                      errors.value ? 'border-red-500' : 'border-gray-300'
                    } ${mode === 'view' ? 'bg-gray-50' : ''}`}
                    placeholder={formData.type === 'percentage' ? '10' : '5000'}
                  />
                </div>
                {errors.value && <p className="text-red-500 text-xs mt-1">{errors.value}</p>}
              </div>
            </div>

            {/* Conditions & Limits */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Conditions & Limits</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Order Amount (Ks)
                </label>
                <input
                  type="number"
                  name="minimum_amount"
                  value={formData.minimum_amount || ''}
                  onChange={handleInputChange}
                  disabled={mode === 'view'}
                  min="0"
                  step="0.01"
                  className={`w-full px-3 py-2 border rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] ${
                    mode === 'view' ? 'bg-gray-50' : 'border-gray-300'
                  }`}
                  placeholder="0"
                />
              </div>

              {formData.type === 'percentage' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Discount Amount (Ks)
                  </label>
                  <input
                    type="number"
                    name="maximum_discount"
                    value={formData.maximum_discount || ''}
                    onChange={handleInputChange}
                    disabled={mode === 'view'}
                    min="0"
                    step="0.01"
                    className={`w-full px-3 py-2 border rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] ${
                      mode === 'view' ? 'bg-gray-50' : 'border-gray-300'
                    }`}
                    placeholder="0"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total Usage Limit
                </label>
                <div className="relative">
                  <UsersIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="number"
                    name="usage_limit"
                    value={formData.usage_limit || ''}
                    onChange={handleInputChange}
                    disabled={mode === 'view'}
                    min="0"
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] ${
                      mode === 'view' ? 'bg-gray-50' : 'border-gray-300'
                    }`}
                    placeholder="Unlimited"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Usage Limit Per Customer
                </label>
                <input
                  type="number"
                  name="usage_limit_per_customer"
                  value={formData.usage_limit_per_customer || ''}
                  onChange={handleInputChange}
                  disabled={mode === 'view'}
                  min="0"
                  className={`w-full px-3 py-2 border rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] ${
                    mode === 'view' ? 'bg-gray-50' : 'border-gray-300'
                  }`}
                  placeholder="Unlimited"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Applies To
                </label>
                <ChoicesSelect
                  options={[
                    { value: 'all', label: 'All Products' },
                    { value: 'specific_products', label: 'Specific Products' },
                    { value: 'specific_categories', label: 'Specific Categories' },
                    { value: 'specific_brands', label: 'Specific Brands' }
                  ]}
                  value={formData.applies_to}
                  onChange={(value) => {
                    const event = { target: { name: 'applies_to', value } };
                    handleInputChange(event as any);
                  }}
                  placeholder="Select Application Scope"
                  disabled={mode === 'view'}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* Date Range */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date *
              </label>
              <div className="relative">
                <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                  disabled={mode === 'view'}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] ${
                    errors.start_date ? 'border-red-500' : 'border-gray-300'
                  } ${mode === 'view' ? 'bg-gray-50' : ''}`}
                />
              </div>
              {errors.start_date && <p className="text-red-500 text-xs mt-1">{errors.start_date}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date *
              </label>
              <div className="relative">
                <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                  disabled={mode === 'view'}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] ${
                    errors.end_date ? 'border-red-500' : 'border-gray-300'
                  } ${mode === 'view' ? 'bg-gray-50' : ''}`}
                />
              </div>
              {errors.end_date && <p className="text-red-500 text-xs mt-1">{errors.end_date}</p>}
            </div>
          </div>

          {/* Status */}
          <div className="mt-6">
            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
                disabled={mode === 'view'}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <label className="ml-2 text-sm font-medium text-gray-700">
                Active Promotion
              </label>
            </div>
          </div>

          {/* View Mode Statistics */}
          {mode === 'view' && promotion && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-sm text-blue-600 font-medium">Type</div>
                <div className="text-lg font-bold text-blue-900">
                  {promotion.type === 'percentage' ? 'Percentage Discount' : 
                   promotion.type === 'fixed_amount' ? 'Fixed Amount Discount' : 
                   promotion.type === 'buy_x_get_y' ? 'Buy X Get Y' : promotion.type}
                </div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-sm text-green-600 font-medium">Times Used</div>
                <div className="text-2xl font-bold text-green-900">{promotion.used_count || 0}</div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="text-sm text-purple-600 font-medium">Usage Remaining</div>
                <div className="text-2xl font-bold text-purple-900">
                  {promotion.usage_limit ? (promotion.usage_limit - (promotion.used_count || 0)) : '∞'}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              {mode === 'view' ? 'Close' : 'Cancel'}
            </button>
            {mode !== 'view' && (
              <button
                type="submit"
                className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                {mode === 'create' ? 'Create Promotion' : 'Update Promotion'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default PromotionModal;
